/**
 * Main App
 */
import React, { useEffect, useLayoutEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import RouterChangeTracker from './routes/common/RouteChangeTracker';

// css
import './lib/reactifyCss';

// app component
import App from './container/App';

import { configureStore } from './store';
import { ThemeProvider, createTheme } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { HelmetProvider } from 'react-helmet-async';

const MainApp = () => {
  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 624,
        md: 900,
        lg: 1200,
        xl: 1536,
        mobile: 0,
        tablet: 768,
        laptop: 1440,
        desktop: 1920,
        basic: 570,
        small: 500,
        medium: 952,
        large: 1000,
        preview: 970,
        mini: 360,
        big: 1320,
        normal: 936,
        right: 400,
        addImage: 450,
        modalMini: 284,
        modalSmall: 411,
        modalMedium: 489,
      },
    },
    palette: {
      error: {
        main: '#FF0000',
      },
      point2: {
        main: '#4bb8f9',
      },
    },
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  });

  useLayoutEffect(() => {
    const height = window.visualViewport?.height || window.innerHeight;
    document.documentElement.style.setProperty('--vh', `${height * 0.01}px`);
  }, []);

  useEffect(() => {
    let ticking = false;

    const setVh = () => {
      if (ticking) return;
      requestAnimationFrame(() => {
        const height = window.visualViewport?.height || window.innerHeight;
        document.documentElement.style.setProperty('--vh', `${height * 0.01}px`);
        ticking = false;
      });
      ticking = true;
    };

    window.visualViewport?.addEventListener('resize', setVh);
    window.visualViewport?.addEventListener('scroll', setVh);

    return () => {
      window.visualViewport?.removeEventListener('resize', setVh);
      window.visualViewport?.removeEventListener('scroll', setVh);
    };
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <Provider store={configureStore()}>
          <ThemeProvider theme={theme}>
            <Router>
              <RouterChangeTracker />
              <Routes>
                <Route path='/*' element={<App />} />
              </Routes>
            </Router>
          </ThemeProvider>
        </Provider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default MainApp;
